import type { PayrollJobBreakdown, OvertimeRateMethod } from '@/lib/types';
import { formatMoney } from '@/lib/currency';

const RATE_SOURCE_LABELS: Record<PayrollJobBreakdown['rate_source'], string> = {
  worker_on_job: 'Worker on this job',
  job: 'Job rate',
  worker_default: 'Worker default',
  missing: 'Missing rate',
  chosen: 'Chosen rate',
  custom: 'Custom rate',
};

export function overtimeMethodLabel(method: OvertimeRateMethod | '' | null | undefined) {
  if (method === 'job') return 'Job rate';
  if (method === 'blended') return 'Blended';
  return '';
}

export function JobBreakdownDisclosure({
  rows,
  currency,
}: {
  rows: PayrollJobBreakdown[];
  currency: string;
}) {
  if (!rows?.length) return null;

  return (
    <details className="rounded border px-3 py-2">
      <summary className="cursor-pointer text-sm font-medium">Per-job breakdown</summary>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="py-1 pr-3">Job</th>
              <th className="py-1 pr-3 text-right">Regular h</th>
              <th className="py-1 pr-3 text-right">OT h</th>
              <th className="py-1 pr-3 text-right">Days</th>
              <th className="py-1 pr-3">Rate</th>
              <th className="py-1 pr-3">Source</th>
              <th className="py-1 pr-3 text-right">Regular pay</th>
              <th className="py-1 pr-3 text-right">OT pay</th>
              <th className="py-1 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.project_id ?? 'none'}-${index}`} className="border-b last:border-0">
                <td className="py-1 pr-3">{row.project_name}</td>
                <td className="py-1 pr-3 text-right">{row.regular_hours}</td>
                <td className="py-1 pr-3 text-right">{row.overtime_hours}</td>
                <td className="py-1 pr-3 text-right">{row.days}</td>
                <td className="py-1 pr-3">
                  {Number(row.day_rate) > 0
                    ? `${formatMoney(row.day_rate, currency)} / day`
                    : `${formatMoney(row.regular_rate, currency)} / h`}
                  {Number(row.overtime_rate) > 0 && (
                    <span className="block text-gray-500">
                      OT {formatMoney(row.overtime_rate, currency)} / h
                    </span>
                  )}
                  {row.rates?.map((rate, rateIndex) => (
                    <span key={`${rate.effective_date}-${rateIndex}`} className="block text-gray-500">
                      {rate.label
                        ? `${rate.label}: ${formatMoney(rate.rate, currency)}`
                        : `${rate.effective_date ?? 'Rate'}: ${formatMoney(rate.rate, currency)}`}
                    </span>
                  ))}
                </td>
                <td className="py-1 pr-3">
                  {row.rate_source_label ?? RATE_SOURCE_LABELS[row.rate_source]}
                </td>
                <td className="py-1 pr-3 text-right">{formatMoney(row.regular_pay, currency)}</td>
                <td className="py-1 pr-3 text-right">{formatMoney(row.overtime_pay, currency)}</td>
                <td className="py-1 text-right font-medium">{formatMoney(row.total, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
